import { NavLink, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Icon, type IconName } from "@/components/icons/Icon";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { TicketsBadge } from "@/features/tickets/TicketsBadge";
import { useDarkMode } from "@/lib/theme";
import { logout } from "@/lib/api";

// v1.1.3 — Sistema de branding eliminado. Sidebar hardcoded.
// rc89 — Restyle Halo Zafiro. rc92 — Paridad EXACTA con el sidebar Halo de
// ByteNMS (mismos tamaños, espaciados y tokens): bg-background + hairline
// (ya no ink-950), halo orbital vía clase CSS .sb-halo (no divs), items 12px
// gap-2.5 iconos 14, labels de grupo 10.5px SIN mayúsculas, activo =
// bg-electric-soft con icono zafiro, brand con subtítulo, tarjeta de usuario
// estilo tarjeta NMS. Fix móvil heredado de NMS (2026-07-15): el aside NO
// lleva `relative` suelto — en <md usa `fixed` (drawer) y un relative lo
// pisaba dejando el sidebar ocupando espacio; en ≥md va `md:relative` que
// además ancla las órbitas del .sb-halo.
const APP_NAME = "ByteISP";

// Cada entrada del nav declara qué permiso requiere para verse.
// Si el rol del user (o sus overrides) no incluye ese permiso, se oculta.
// `perm: null` = visible para todos los logueados.
type NavItem = {
  to: string;
  label: string;
  /** rc56: icon como nombre del sprite outline (en lugar de componente Lucide). */
  iconName: IconName;
  end?: boolean;
  perm: string | null;
  /** Componente opcional que se renderea al lado derecho del item
   * (ej. badge contador). El componente se monta cuando el item es
   * visible, así puede consumir hooks (TanStack Query, etc.) por su
   * cuenta sin acoplar al Sidebar. */
  Badge?: React.ComponentType;
};

// F100 — Sidebar agrupado en cascada (solo visual). Las rutas siguen
// planas (`/clientes`, `/contratos`, etc.) — el grupo solo agrupa
// visualmente. Si un grupo no tiene items visibles tras filtro de
// permisos, su header tampoco se renderiza.
type NavGroup = {
  label: string | null;  // null = sin header (root: solo Dashboard)
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard", iconName: "dashboard", end: true, perm: "dashboard.read" },
    ],
  },
  {
    label: "Operación",
    items: [
      { to: "/clientes",   label: "Clientes",   iconName: "users",     perm: "clientes.read" },
      { to: "/prospectos", label: "Prospectos", iconName: "user",      perm: "prospectos.read" },
      { to: "/contratos",  label: "Contratos",  iconName: "file-text", perm: "contratos.read" },
      { to: "/tickets",   label: "Tickets",   iconName: "ticket",    perm: "tickets.read", Badge: TicketsBadge },
      { to: "/sitios",    label: "Sitios",    iconName: "map-pin",   perm: "clientes.read" },
      // rc102 — mapa de clientes/contratos con GPS
      { to: "/mapa",      label: "Mapa",      iconName: "map-pin",   perm: "contratos.read" },
      // rc104 — inventario de comodatos (pendientes de recolección)
      { to: "/equipos",   label: "Equipos",   iconName: "package",   perm: "contratos.read" },
    ],
  },
  {
    label: "Red",
    items: [
      { to: "/planes",  label: "Planes",  iconName: "package", perm: "planes.read" },
      { to: "/redes",   label: "Redes",   iconName: "network", perm: "redes.read" },
      { to: "/routers", label: "Routers", iconName: "router",  perm: "routers.read" },
      { to: "/vpn",     label: "VPN",     iconName: "shield",  perm: "vpn.read" },
    ],
  },
  {
    label: "Finanzas",
    items: [
      { to: "/facturas", label: "Facturas", iconName: "receipt",     perm: "facturas.read" },
      { to: "/pagos",    label: "Pagos",    iconName: "wallet",      perm: "pagos.read" },
      { to: "/cobranza", label: "Cobranza", iconName: "phone-call",  perm: "cobranza.read" },
      { to: "/promesas", label: "Promesas", iconName: "calendar",    perm: "cobranza.read" },
      // rc98 — módulo Análisis (resúmenes ejecutivos, solo analisis.read)
      { to: "/analisis", label: "Análisis", iconName: "activity",    perm: "analisis.read" },
    ],
  },
  {
    label: "Sistema",
    items: [
      { to: "/usuarios",    label: "Usuarios",    iconName: "user-cog",  perm: "usuarios.read" },
      { to: "/auditoria",   label: "Auditoría",   iconName: "history",   perm: "usuarios.read" },
      { to: "/importacion", label: "Importación", iconName: "database",  perm: "usuarios.write" },
      { to: "/ajustes",     label: "Ajustes",     iconName: "settings",  perm: "usuarios.read" },
    ],
  },
];

type Props = {
  collapsed: boolean;
  onToggle: () => void;
  /** Drawer móvil (<md): abierto/cerrado. En desktop no aplica. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: Props) {
  const permissions = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const { data: brand } = useBranding();  // rc129 — nombre comercial del ISP
  const isCajero = user?.rol?.nombre === "cajero";
  // rc136 — en móvil el tema y "salir" viven aquí abajo (la barra superior va limpia)
  const { isDark, toggle: toggleTheme } = useDarkMode();
  const navigate = useNavigate();
  async function salir() {
    await logout();
    navigate("/login", { replace: true });
  }

  // rc89 — iniciales pa' el avatar de la tarjeta de usuario Halo
  const userInitials = useMemo(() => {
    const src = user?.nombre_completo || user?.email || "BI";
    const parts = src.trim().split(/\s+/);
    const ini = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : src.slice(0, 2);
    return ini.toUpperCase();
  }, [user]);

  // F87: cajero ve solo Dashboard en el sidebar (su flujo va por Pago Rápido).
  // Las páginas que necesita (clientes, facturas, pagos) siguen accesibles
  // por URL/atajos pero no atulan el menú.
  // F100: ahora agrupado — filtra items por permiso y omite grupos vacíos.
  const visibleGroups = useMemo(() => {
    return NAV_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        if (isCajero) return item.to === "/";
        return !item.perm || permissions.includes(item.perm);
      }),
    })).filter((g) => g.items.length > 0);
  }, [permissions, isCajero]);

  return (
    <aside
      className={cn(
        // rc92 — paridad NMS: hairline + halo CSS (.sb-halo). rc146: fondo de vidrio (.glass-bar).
        // OJO: sin `relative` suelto (rompe el drawer fixed en móvil);
        // en desktop `md:relative` ancla las órbitas del halo.
        "sb-halo flex flex-col border-r border-border glass-bar text-muted-foreground",
        "transition-[width,transform] duration-200 ease-out",
        "fixed inset-y-0 left-0 z-40 max-md:w-56 md:relative",
        // rc146.1 — Glass Zafiro: en escritorio la barra flota como panel de vidrio
        "md:my-3 md:ml-3 md:h-[calc(100%-1.5rem)] md:rounded-[14px] md:border md:shadow-[inset_0_1px_0_hsl(0_0%_100%/0.05),0_10px_30px_rgb(0_0_0/0.18)]",
        "max-md:pt-[env(safe-area-inset-top)]",  // rc135.1 — PWA iOS: no meter el logo bajo la barra de estado
        mobileOpen ? "translate-x-0" : "max-md:-translate-x-full",
        collapsed ? "md:w-[56px]" : "md:w-56",
      )}
    >
      {/* Brand header — mismo bloque que NMS (logo + nombre + subtítulo) */}
      <div className={cn("flex items-start justify-between px-4 pb-3 pt-4", collapsed && "px-3")}>
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            {/* rc89 — logo de barras Halo (match mockup) */}
            <div className="flex h-7 w-7 items-center justify-center gap-[2.5px] rounded-lg bg-electric-soft">
              <span className="block h-[9px] w-[3px] rounded-full bg-electric opacity-80" />
              <span className="block h-[14px] w-[3px] rounded-full bg-electric" />
              <span className="block h-[7px] w-[3px] rounded-full bg-electric opacity-55" />
            </div>
            <div>
              <div className="text-[12px] font-semibold leading-tight text-foreground">
                Byte<span className="text-electric">ISP</span>
              </div>
              <div className="text-[10px] leading-tight text-muted-foreground/60">
                {brand?.nombre ?? ""} · ISP
              </div>
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground max-md:hidden"
          aria-label="Toggle sidebar"
        >
          <Icon
            name="chevron-left"
            size={14}
            className={cn("transition-transform", collapsed && "rotate-180")}
          />
        </button>
        {/* rc136 — móvil: X para cerrar el cajón */}
        <button
          onClick={onMobileClose}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:hidden"
          aria-label="Cerrar menú"
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      {/* Nav agrupado (F100) — click en un link cierra el drawer móvil.
          rc92 — espaciados/tamaños espejo de NMS: space-y-3 entre grupos,
          labels 10.5px sin uppercase, items 12px gap-2.5 iconos 14. */}
      <nav className="flex-1 space-y-3 overflow-y-auto px-2 py-2" onClick={onMobileClose}>
        {visibleGroups.map((group, gi) => (
          <div key={group.label ?? `_root${gi}`} className="space-y-0.5">
            {/* Header de grupo — solo cuando no está colapsado y tiene label */}
            {group.label && !collapsed && (
              <div className="px-2 pt-2.5 pb-1 text-[10.5px] font-medium uppercase tracking-[.07em] text-muted-foreground">
                {group.label}
              </div>
            )}
            {/* Separador sutil entre grupos cuando está colapsado */}
            {group.label && collapsed && gi > 0 && (
              <div className="mx-2 my-1 h-px bg-border" />
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        // rc92: item activo = píldora zafiro suave con icono
                        // zafiro (espejo exacto del NavItem de NMS).
                        // rc93: line-height 16px explícito — NMS usa text-xs (12/16);
                        // el arbitrary text-[12px] heredaba 1.5 (18px) y cada fila
                        // quedaba ~2px más alta que la de NMS.
                        "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[12px]/[16px] transition-colors",
                        isActive
                          ? "bg-primary/15 font-medium text-foreground ring-1 ring-inset ring-primary/25 [&_svg]:text-electric"
                          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                        collapsed && "justify-center",
                      )
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon name={item.iconName} size={14} className="shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {!collapsed && item.Badge && <item.Badge />}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* rc89 — Tarjeta de usuario Halo · rc92 estilo tarjeta NMS
          (rounded-xl border-border bg-card, mx-2 mb-2.5) */}
      {/* rc136 — móvil: tema + cerrar sesión al pie del cajón */}
      <div className="mx-2 mb-1 grid grid-cols-2 gap-1.5 md:hidden">
        <button
          type="button"
          onClick={toggleTheme}
          className="flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-2 py-2 text-[11px] text-foreground"
        >
          <Icon name={isDark ? "sun" : "moon"} size={13} />
          {isDark ? "Tema claro" : "Tema oscuro"}
        </button>
        <button
          type="button"
          onClick={salir}
          className="flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-2 py-2 text-[11px] text-destructive"
        >
          <Icon name="log-out" size={13} />
          Cerrar sesión
        </button>
      </div>
      {!collapsed ? (
        <div className="mx-2 mb-2.5 mt-1 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 max-md:mb-[calc(0.625rem+env(safe-area-inset-bottom))]">
          <div className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-electric-soft text-[10px] font-semibold text-electric">
            {userInitials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] font-medium leading-tight text-foreground">
              {user?.nombre_completo || user?.email || APP_NAME}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] leading-tight text-muted-foreground/60">
              <span className="halo-pulse inline-block h-[5px] w-[5px] rounded-full bg-success" />
              en línea
            </div>
          </div>
          <span className="font-mono text-[10px] text-muted-foreground/60">v{__APP_VERSION__}</span>
        </div>
      ) : (
        <div className="mx-auto mb-2.5 flex h-7 w-7 items-center justify-center rounded-lg bg-electric-soft text-[10px] font-semibold text-electric">
          {userInitials}
        </div>
      )}
    </aside>
  );
}
